import '../js/app';

import './polyfills';
import './global.events';
import * as angular from 'angular';
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { downgradeInjectable, UpgradeModule } from '@angular/upgrade/static';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { TranslateModule, TranslateService, TranslateCompiler } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { AccountModule } from './account/account.module';
import { ClustersModule } from './clusters/clusters.module';
import { CommonModule } from './common/common.module';
import { ConnectionsModule } from './connections/connections.module';
import { PigModule } from './pig/pig.module';
import { PackagesModule } from './packages/packages.module';
import { CustomHttpInterceptor } from './config/custom-http.interceptor';
import { RollbarService, rollbarFactory, RollbarErrorHandler } from './config/rollbar';
import { SettingsModule } from './settings/settings.module';
import { AppComponent } from './app.component';
import { JobsModule } from './jobs/jobs.module';
import en from '../locales/en';
import { SchedulesModule } from './schedules/schedules.module';
import { WorkspacesModule } from './workspaces/workspaces.module';
import { HelpModule } from './help/help.module';
import { PackageDesignerModule } from './package-designer/package-designer.module';

((window as any).xplenty as angular.IModule).factory('ngrxStoreService', downgradeInjectable(Store));

@NgModule({
  imports: [
    BrowserModule,
    UpgradeModule,
    HttpClientModule,
    AccountModule,
    PackageDesignerModule,
    ClustersModule,
    CommonModule,
    ConnectionsModule,
    HelpModule,
    JobsModule,
    PackagesModule,
    PigModule,
    SettingsModule,
    SchedulesModule,
    WorkspacesModule,
    StoreModule.forRoot({}),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
    }),
    EffectsModule.forRoot(),
    TranslateModule.forRoot(),
    LoadingBarHttpClientModule,
    LoadingBarModule,
    TranslateModule.forRoot({
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
    NgxPermissionsModule.forRoot(),
  ],
  declarations: [AppComponent],
  entryComponents: [AppComponent],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptor,
      multi: true,
    },
    {
      provide: '$rootScope',
      useFactory: ($injector: any) => $injector.get('$rootScope'),
      deps: ['$injector'],
    },
    {
      provide: '$stateParams',
      useFactory: ($injector: any) => $injector.get('$stateParams'),
      deps: ['$injector'],
    },
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
  ],
})
export class AppModule {
  constructor(private readonly translate: TranslateService, private permissionsService: NgxPermissionsService) {}
  // Override Angular bootstrap so it doesn't do anything
  // eslint-disable-next-line class-methods-use-this
  ngDoBootstrap() {
    this.translate.setTranslation('en', en);
    this.translate.setDefaultLang('en');
    this.translate.use('en');

    const perm = [];
    this.permissionsService.loadPermissions(perm);
  }
}

// Bootstrap using the UpgradeModule
platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .then((platformRef) => {
    // eslint-disable-next-line no-console
    console.log('Bootstrapping in Hybrid mode with Angular & AngularJS');
    const upgrade = platformRef.injector.get(UpgradeModule) as UpgradeModule;
    upgrade.bootstrap(document.querySelector('html'), ['xplenty']);
  });
